import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, Trash2, ExternalLink, Loader2, RefreshCw, Briefcase, Eye } from "lucide-react";
import { API } from "@/App";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PORTFOLIO_SEED } from "@/data/portfolioIntelligence";
import { format } from "date-fns";

const FILTER_TABS = [
  { id: "all",       label: "All" },
  { id: "portfolio",  label: "Portfolio" },
  { id: "watchlist",  label: "Watchlist" },
];

export default function FlaggedCompanies() {
  const navigate = useNavigate();
  const [flaggedCompanies, setFlaggedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const portfolioIds = useMemo(() => new Set(Object.keys(PORTFOLIO_SEED)), []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const flagRes = await fetch(`${API}/companies/flagged`);
      if (flagRes.ok) {
        const d = await flagRes.json();
        setFlaggedCompanies(d.flagged_companies || []);
      }
    } catch (e) {
      console.error("Failed to fetch data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFlag = async (companyId) => {
    setRemoving(companyId);
    try {
      const res = await fetch(`${API}/companies/unflag/${companyId}`, { method: "DELETE" });
      if (res.ok) setFlaggedCompanies(prev => prev.filter(c => c.company_id !== companyId));
    } catch (e) {
      console.error("Failed to unflag company:", e);
    } finally {
      setRemoving(null);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Classify each company
  // Since Watchlist mirrors Portfolio, all PORTFOLIO_SEED companies are both.
  // For non-seed companies, use source_feed from the flagging context.
  const classified = useMemo(() =>
    flaggedCompanies.map(c => {
      const inSeed = portfolioIds.has(c.company_id);
      const sourceFeed = c.source_feed || "portfolio";
      return {
        ...c,
        isPortfolio: inSeed || sourceFeed === "portfolio",
        isWatchlist: inSeed || sourceFeed === "watchlist",
      };
    }),
    [flaggedCompanies, portfolioIds]
  );

  // Counts
  const counts = useMemo(() => ({
    all: classified.length,
    portfolio: classified.filter(c => c.isPortfolio).length,
    watchlist: classified.filter(c => c.isWatchlist).length,
  }), [classified]);

  // Filtered list
  const filtered = useMemo(() => {
    if (activeFilter === "portfolio") return classified.filter(c => c.isPortfolio);
    if (activeFilter === "watchlist") return classified.filter(c => c.isWatchlist);
    return classified;
  }, [classified, activeFilter]);

  return (
    <div style={{ padding: "32px 40px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Flag size={24} style={{ color: "#F59E0B" }} />
          <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#0A0A0A", fontFamily: "'Playfair Display', serif" }}>
            Flagged Companies
          </h1>
        </div>
        <button onClick={fetchData}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", borderRadius: "6px", border: "1px solid #E5E7EB", background: "#FFFFFF", fontSize: "12px", fontWeight: 500, color: "#374151", cursor: "pointer" }}
          data-testid="refresh-flagged">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Description */}
      <p style={{ fontSize: "14px", color: "#6B7280", marginBottom: "24px" }}>
        Companies flagged from news stories for weekly review. These companies have been marked for closer monitoring based on recent market developments.
      </p>

      {/* Filter Tabs */}
      {!loading && flaggedCompanies.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "20px" }} data-testid="flagged-filter-tabs">
          {FILTER_TABS.map(tab => {
            const isActive = activeFilter === tab.id;
            const count = counts[tab.id];
            const accent = tab.id === "portfolio" ? "#16A34A" : tab.id === "watchlist" ? "#3B82F6" : "#374151";
            return (
              <button key={tab.id} onClick={() => setActiveFilter(tab.id)}
                data-testid={`flagged-tab-${tab.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "7px 16px", borderRadius: "6px", fontSize: "13px", fontWeight: 600,
                  cursor: "pointer", transition: "all 120ms",
                  background: isActive ? (tab.id === "portfolio" ? "#F0FDF4" : tab.id === "watchlist" ? "#EFF6FF" : "#F3F4F6") : "#FFFFFF",
                  border: `1px solid ${isActive ? accent : "#E5E7EB"}`,
                  color: isActive ? accent : "#6B7280",
                }}>
                {tab.id === "portfolio" && <Briefcase size={13} />}
                {tab.id === "watchlist" && <Eye size={13} />}
                {tab.label}
                <span style={{
                  fontSize: "11px", fontWeight: 700, padding: "1px 7px", borderRadius: "999px",
                  background: isActive ? accent : "#E5E7EB",
                  color: isActive ? "#FFFFFF" : "#6B7280",
                }}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0" }}>
          <Loader2 size={24} className="animate-spin" style={{ color: "#97C8A6" }} />
        </div>
      )}

      {/* Empty State */}
      {!loading && flaggedCompanies.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#FAFBFC", borderRadius: "8px", border: "1px dashed #E5E7EB" }}>
          <Flag size={40} style={{ color: "#D1D5DB", marginBottom: "16px" }} />
          <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>No Flagged Companies</h3>
          <p style={{ fontSize: "13px", color: "#9AA0A6", marginBottom: "16px" }}>Flag companies from story pages to add them to your weekly review list.</p>
          <button onClick={() => navigate("/briefing")}
            style={{ padding: "10px 20px", borderRadius: "6px", border: "none", background: "#97C8A6", color: "#181B20", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
            Browse News Feed
          </button>
        </div>
      )}

      {/* Filtered empty */}
      {!loading && flaggedCompanies.length > 0 && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 20px", background: "#FAFBFC", borderRadius: "8px", border: "1px dashed #E5E7EB" }}>
          <p style={{ fontSize: "14px", color: "#6B7280" }}>
            No flagged companies in your <strong>{activeFilter === "portfolio" ? "Portfolio" : "Watchlist"}</strong>.
          </p>
          <button onClick={() => setActiveFilter("all")}
            style={{ marginTop: "12px", fontSize: "13px", fontWeight: 500, color: "#3B82F6", background: "none", border: "none", cursor: "pointer" }}
            data-testid="flagged-show-all">
            Show all flagged companies
          </button>
        </div>
      )}

      {/* Flagged Companies List */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {filtered.map(company => {
            const borderColor = company.isPortfolio && company.isWatchlist
              ? "linear-gradient(180deg, #16A34A 0%, #3B82F6 100%)"
              : company.isPortfolio ? "#16A34A"
              : company.isWatchlist ? "#3B82F6"
              : "#E5E7EB";
            const usesGradient = company.isPortfolio && company.isWatchlist;

            return (
              <div key={company.company_id}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "16px 20px", background: "#FFFFFF", borderRadius: "8px",
                  border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  borderLeft: usesGradient ? "none" : `4px solid ${borderColor}`,
                  position: "relative", overflow: "hidden",
                }}
                data-testid={`flagged-company-${company.company_id}`}>

                {/* Gradient left border for dual membership */}
                {usesGradient && (
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, width: "4px",
                    background: borderColor,
                  }} />
                )}

                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <CompanyLogo name={company.company_name} permalink={company.company_id} size={36} />
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
                      <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#0A0A0A", margin: 0 }}>
                        {company.company_name}
                      </h3>
                      {company.isPortfolio && (
                        <span data-testid={`badge-portfolio-${company.company_id}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "4px",
                            fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "999px",
                            background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0",
                            letterSpacing: "0.03em",
                          }}>
                          <Briefcase size={10} /> PORTFOLIO
                        </span>
                      )}
                      {company.isWatchlist && (
                        <span data-testid={`badge-watchlist-${company.company_id}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "4px",
                            fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "999px",
                            background: "#EFF6FF", color: "#3B82F6", border: "1px solid #BFDBFE",
                            letterSpacing: "0.03em",
                          }}>
                          <Eye size={10} /> WATCHLIST
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: "12px", color: "#6B7280", margin: 0 }}>
                      Flagged from: <span style={{ fontWeight: 500 }}>{company.story_title || "News story"}</span>
                    </p>
                    <p style={{ fontSize: "11px", color: "#9AA0A6", marginTop: "2px" }}>
                      {company.flagged_at && format(new Date(company.flagged_at), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                  {company.story_id && (
                    <button onClick={() => navigate(`/story/${company.story_id}`)}
                      style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", borderRadius: "6px", border: "1px solid #E5E7EB", background: "#FFFFFF", fontSize: "12px", fontWeight: 500, color: "#374151", cursor: "pointer" }}
                      data-testid={`view-story-${company.company_id}`}>
                      <ExternalLink size={14} /> View Story
                    </button>
                  )}
                  {company.isPortfolio && (
                    <button onClick={() => navigate(`/portfolio-matrix?company=${encodeURIComponent(company.company_name)}`)}
                      style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 14px", borderRadius: "6px", border: "1px solid #BBF7D0", background: "#F0FDF4", fontSize: "12px", fontWeight: 500, color: "#16A34A", cursor: "pointer" }}
                      data-testid={`go-portfolio-${company.company_id}`}>
                      <Briefcase size={12} /> Portfolio
                    </button>
                  )}
                  {company.isWatchlist && (
                    <button onClick={() => navigate(`/watchlist-matrix?company=${encodeURIComponent(company.company_name)}`)}
                      style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 14px", borderRadius: "6px", border: "1px solid #BFDBFE", background: "#EFF6FF", fontSize: "12px", fontWeight: 500, color: "#3B82F6", cursor: "pointer" }}
                      data-testid={`go-watchlist-${company.company_id}`}>
                      <Eye size={12} /> Watchlist
                    </button>
                  )}
                  <button onClick={() => handleRemoveFlag(company.company_id)}
                    disabled={removing === company.company_id}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", borderRadius: "6px", border: "1px solid #FECACA", background: "#FEF2F2", fontSize: "12px", fontWeight: 500, color: "#DC2626", cursor: removing === company.company_id ? "default" : "pointer", opacity: removing === company.company_id ? 0.5 : 1 }}
                    data-testid={`remove-flag-${company.company_id}`}>
                    {removing === company.company_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {!loading && flaggedCompanies.length > 0 && (
        <div style={{ marginTop: "24px", padding: "16px 20px", background: "#F9FAFB", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <p style={{ fontSize: "13px", color: "#6B7280", margin: 0 }}>
              <strong style={{ color: "#374151" }}>{flaggedCompanies.length}</strong> companies flagged for weekly review
            </p>
            {counts.portfolio > 0 && (
              <span style={{ fontSize: "11px", fontWeight: 600, color: "#16A34A", background: "#F0FDF4", padding: "3px 10px", borderRadius: "999px", border: "1px solid #BBF7D0" }}>
                {counts.portfolio} Portfolio
              </span>
            )}
            {counts.watchlist > 0 && (
              <span style={{ fontSize: "11px", fontWeight: 600, color: "#3B82F6", background: "#EFF6FF", padding: "3px 10px", borderRadius: "999px", border: "1px solid #BFDBFE" }}>
                {counts.watchlist} Watchlist
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => navigate("/portfolio-matrix")}
              style={{ padding: "8px 14px", borderRadius: "6px", border: "none", background: "#97C8A6", color: "#181B20", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
              View in Portfolio Matrix
            </button>
            <button onClick={() => navigate("/watchlist-matrix")}
              style={{ padding: "8px 14px", borderRadius: "6px", border: "1px solid #60A5FA", background: "#FFFFFF", color: "#60A5FA", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
              data-testid="flagged-go-to-watchlist-matrix">
              View in Watchlist Matrix
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
